import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  makeStyles
} from '@material-ui/core';
import {
  DeleteForever as DeleteForeverIcon,
  Description as DocIcon,
  PictureAsPdf as PdfIcon,
  Photo as ImageIcon,
  Movie as VideoIcon,
  Audiotrack as AudioIcon,
  Archive as ZipIcon,
  TableChart as ExcelIcon,
  Assignment as WordIcon,
  Equalizer as PowerpointIcon,
  Code as CodeIcon,
  Close as CloseIcon
} from '@material-ui/icons';
import { confirmAlert } from 'react-confirm-alert';
import ReactPanZoom from 'react-image-pan-zoom-rotate';
import axios from 'src/utils/axios';
import { openBlob } from 'src/utils/Common';
import { deleteFotografWithId } from 'src/utils/storage/StrPhoto';
import 'react-confirm-alert/src/react-confirm-alert.css';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import logger from 'src/utils/logger';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    width: '100%'
  },
  fileCard: {
    position: 'relative',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    border: '1px solid #f0f0f0',
    transition: 'all 0.2s ease-in-out',
    '&:active': {
      transform: 'scale(0.95)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
  },
  mediaWrapper: {
    height: 110,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fafafa',
    position: 'relative',
    cursor: 'pointer'
  },
  iconLarge: {
    fontSize: 48,
  },
  cardContent: {
    padding: '10px !important',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  fileName: {
    fontWeight: 600,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: '#333'
  },
  fileTypeLabel: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    marginTop: 2
  },
  deleteButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: 'rgba(255,255,255,0.9)',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    padding: 6,
    '&:hover': { backgroundColor: '#fff' }
  },
  previewDialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#111',
      color: '#fff'
    }
  }
}));

const FilesPreview = ({ className, files = [], readonly, fetchPhotos, setLoading, issondaj = 0 }) => {
  const classes = useStyles();
  const [alertState, setAlertState] = useState({ open: false, message: '' });
  const [previewState, setPreviewState] = useState({ open: false, url: '', fileName: '' });

  // TÜM DOSYA TİPLERİ İÇİN GELİŞMİŞ EŞLEŞTİRİCİ
  const getFileStyle = (fileType, fileName = "") => {
    const type = fileType?.toLowerCase() || "";
    const name = fileName?.toLowerCase() || "";

    if (type.startsWith('image/')) return { icon: <ImageIcon className={classes.iconLarge} />, color: '#4caf50', label: 'RESİM' };
    if (type === 'application/pdf' || name.endsWith('.pdf')) return { icon: <PdfIcon className={classes.iconLarge} />, color: '#f44336', label: 'PDF' };
    if (type.includes('excel') || type.includes('spreadsheet') || name.match(/\.(xls|xlsx|csv)$/)) return { icon: <ExcelIcon className={classes.iconLarge} />, color: '#1d6f42', label: 'EXCEL' };
    if (type.includes('word') || name.match(/\.(doc|docx)$/)) return { icon: <WordIcon className={classes.iconLarge} />, color: '#2b579a', label: 'WORD' };
    if (type.includes('presentation') || name.match(/\.(ppt|pptx)$/)) return { icon: <PowerpointIcon className={classes.iconLarge} />, color: '#d24726', label: 'SLAYT' };
    if (type.startsWith('video/')) return { icon: <VideoIcon className={classes.iconLarge} />, color: '#673ab7', label: 'VİDEO' };
    if (type.startsWith('audio/')) return { icon: <AudioIcon className={classes.iconLarge} />, color: '#00bcd4', label: 'SES' };
    if (type.includes('zip') || type.includes('compressed') || name.match(/\.(zip|rar|7z)$/)) return { icon: <ZipIcon className={classes.iconLarge} />, color: '#ff9800', label: 'ARŞİV' };
    if (name.match(/\.(js|html|css|json|py|sql)$/)) return { icon: <CodeIcon className={classes.iconLarge} />, color: '#3f51b5', label: 'KOD' };

    return { icon: <DocIcon className={classes.iconLarge} />, color: '#757575', label: 'DOSYA' };
  };

  const handleFileClick = async (dosya) => {
    if (dosya.DOSYA_ID < 0) {
      const w = window.open("");
      w.document.write(`<img src="${dosya.DOSYA_DATA}" style="max-width:100%;" />`);
      return;
    }

    setLoading(true);
    try {
      const endpoint = issondaj === 1
        ? `/FileImportExport/ArtemisSondajDosya/${dosya.DOSYA_ID}/${issondaj}`
        : `/FileImportExport/ArtemisDosya/${dosya.DOSYA_ID}`;

      const response = await axios.get(endpoint, { responseType: 'blob' });
      const blobData = response.data;
      if (dosya.DOSYA_ADI.match(/\.(jpeg|JPEG|jpg|gif|png)$/i)) {
        const imgUrl = URL.createObjectURL(new Blob([blobData]));
        setPreviewState({ open: true, url: imgUrl, fileName: dosya.DOSYA_ADI });
      } else {
        openBlob(blobData, dosya.DOSYA_ADI);
      }
    } catch (error) {
      logger.error("Hata:", error);
    } finally {
      setLoading(false);
    }
  };

  const executeDelete = async (id, fisNo) => {
    try {
      if (id < 0) {
        await deleteFotografWithId(id);
      } else {
        const url = issondaj === 1 ? `/DeleteArtemisSondajDosya/${id}/${fisNo}` : `/DeleteArtemisDosya/${id}/${fisNo}`;
        const response = await axios.get(url);
        setAlertState({ open: true, message: response.data?.ErrorDescription || "Silme işlemi başarılı." });
      }
      fetchPhotos(true);
    } catch (error) {
      fetchPhotos(true);
    }
  };

  return (
    <div className={clsx(classes.root, className)}>
      <Grid container spacing={2}>
        {files.map((file) => {
          const styleInfo = getFileStyle(file.DOSYA_TIPI, file.DOSYA_ADI);
          return (
            <Grid item xs={6} sm={4} md={3} key={file.DOSYA_ID}>
              <Card className={classes.fileCard}>
                <Box
                  className={classes.mediaWrapper}
                  onClick={() => handleFileClick(file)}
                  style={{ borderBottom: `3px solid ${styleInfo.color}` }}
                >
                  {React.cloneElement(styleInfo.icon, { style: { color: styleInfo.color } })}

                  {!readonly && (
                    <IconButton
                      className={classes.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmAlert({
                          title: 'Silme Onayı',
                          message: 'Bu dosya kalıcı olarak silinecek.',
                          buttons: [
                            { label: 'Evet', onClick: () => executeDelete(file.DOSYA_ID, file.DOSYA_FIS_NO) },
                            { label: 'Hayır' }
                          ],
                        });
                      }}
                    >
                      <DeleteForeverIcon fontSize="small" color="error" />
                    </IconButton>
                  )}
                </Box>
                <CardContent className={classes.cardContent} onClick={() => handleFileClick(file)}>
                  <Typography className={classes.fileName}>{file.DOSYA_ADI}</Typography>
                  <Typography className={classes.fileTypeLabel} style={{ color: styleInfo.color }}>
                    {styleInfo.label} {file.DOSYA_ID < 0 && '• LOKAL'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Modern Önizleme Modalı */}
      <Dialog
        fullScreen
        open={previewState.open}
        onClose={() => setPreviewState({ ...previewState, open: false })}
      >
        {/* AppBar kullanarak başlığı ve butonu standart bir yapıya oturtuyoruz */}
        <AppBar style={{ position: 'relative', backgroundColor: '#222' }}>
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              variant="h6"
              style={{
                flex: 1,
                color: '#fff',
                fontSize: '0.9rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginRight: '10px'
              }}
            >
              {previewState.fileName}
            </Typography>

            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setPreviewState({ ...previewState, open: false })}
              aria-label="close"
              style={{ color: '#FF9800', backgroundColor: 'rgba(255, 152, 0, 0.1)' }} // Butonu belirginleştirmek için hafif arka plan
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            backgroundColor: '#000',
            overflow: 'hidden'
          }}
        >
          <ReactPanZoom image={previewState.url} alt={previewState.fileName} />
        </DialogContent>
      </Dialog>


      {/* Bilgi Alerti */}
      <Dialog open={alertState.open} onClose={() => setAlertState({ ...alertState, open: false })}>
        <DialogTitle>Bilgilendirme</DialogTitle>
        <Divider />
        <DialogContent><Typography>{alertState.message}</Typography></DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={() => setAlertState({ ...alertState, open: false })} color="secondary" variant="contained">Tamam</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

FilesPreview.propTypes = {
  files: PropTypes.array,
  readonly: PropTypes.bool,
  fetchPhotos: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  issondaj: PropTypes.number
};

export default FilesPreview;